import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Text, Title } from '@dropbox/dig-components/typography';
import { Box, Stack } from '@dropbox/dig-foundations';
import { getBrandName } from 'js/sign-components/common/brand';
import { useSiteCode } from 'js/sign-components/common/use-site-code';
import { Button } from '@dropbox/dig-components/buttons';
import HfReactHelper from 'js/sign-components/common/hf-react-helper';
import Highlights from 'hellospa/components/payment/highlights';
import { useAuthentication } from 'signer-app/authentication';
import './progressive-checkout-plan-info.scss';

const NextHigherPlan: React.FunctionComponent<{
  name: string;
  higherPlanId: string | null;
}> = ({ name, higherPlanId }) => {
  let text = null;
  let linkText = null;
  let link = '';

  if (name === 'Essentials') {
    text = (
      <FormattedMessage
        id=""
        defaultMessage="Looking for a team plan?"
        description="label, label for button/link that will allow user to purchase higher tier plan"
      />
    );
    linkText = (
      <FormattedMessage
        id=""
        defaultMessage="Switch to Standard"
        description="link, label, will trasnfer user to buying higher tier plan. Standard is noun. Standard is plan name"
      />
    );
    link = `/pricing/selectPlanForSignUp?plan_id=${higherPlanId}&period=Y&source=hs-pricing`;
  }
  if (name === 'Standard') {
    text = (
      <FormattedMessage
        id=""
        defaultMessage="Looking for a plan for a larger team?"
        description="label, label for button/link that will allow user to purchase higher tier plan"
      />
    );
    linkText = (
      <FormattedMessage
        id=""
        defaultMessage="Get custom quote"
        description="link, label, will trasnfer user to contacting customer success representative"
      />
    );
    link = HfReactHelper.getWebflowUrl('form/contact-us-general');
  }

  return (
    <>
      <Text>{text}</Text>
      <Box
        paddingLeft="Micro Small"
        display={{ xsmall: 'none', medium: 'inline' }}
      >
        <Button href={link} variant="transparent" size="small">
          <Text color="inherit" isBold={true}>
            {linkText}
          </Text>
        </Button>
      </Box>
      <Box display={{ xsmall: 'block', medium: 'none' }} paddingTop="12">
        <Button
          href={link}
          variant="outline"
          fullWidth={false}
          hasNoUnderline={false}
        >
          {linkText}
        </Button>
      </Box>
    </>
  );
};

const ProgressiveCheckoutPlanInfo: React.FunctionComponent = () => {
  const { meta } = useAuthentication();
  const siteCode = useSiteCode();

  if (!meta || !meta.planData) {
    return null;
  }
  const plan = meta.planData;

  const title =
    plan.name === 'Essentials' ? (
      <FormattedMessage
        id=""
        defaultMessage="What you'll get with Essentials"
        description="title, information header regarding plan to be purchased. Essentials is noun. Essentials is a name of a plan"
      />
    ) : (
      <FormattedMessage
        id=""
        defaultMessage="What you'll get with Standard"
        description="title, information header regarding plan to be purchased. Standard is noun. Standard is a name of a plan"
      />
    );

  const marketingText =
    plan.name === 'Essentials' ? (
      <FormattedMessage
        id=""
        defaultMessage="The {brandName} Essentials plan is perfect for individuals who want to save time and simplify processes, all while minimizing room for error."
        description="marketing statement, description of a plan to be purchased. Essentials is noun. Essentials is a name of a plan"
        values={{ brandName: getBrandName(siteCode) }}
      />
    ) : (
      <FormattedMessage
        id=""
        defaultMessage="The {brandName} Standard plan is perfect for small teams looking for more features to help them collaborate and get work done."
        description="marketing statement, description of a plan to be purchased. Standard is noun. Standard is a name of a plan"
        values={{ brandName: getBrandName(siteCode) }}
      />
    );

  return (
    <>
      <Stack gap={{ xsmall: 'Macro Medium', medium: 'Macro Large' }}>
        <Box>
          <Title>{title}</Title>
        </Box>
        <Box>
          <Text>{marketingText}</Text>
        </Box>
        <Highlights plan={plan} />
        <Box>
          <NextHigherPlan
            name={plan.name ?? ''}
            higherPlanId={plan.nextPlan.id}
          />
        </Box>
      </Stack>
    </>
  );
};

export default ProgressiveCheckoutPlanInfo;
